import { useCallback, useMemo } from "react";
import { useUser } from "../context/UserContext";
import { PERSONA } from "../constants/permissions";

/**
 * Centralised permission hook. Replaces usePrivilege.
 * Reads userInfo.permissions populated from /me/permissions (or from the login response).
 *
 * Shape:
 *   userInfo.permissions = {
 *     persona: string,
 *     isDeveloperAdmin: boolean,
 *     projectIds: number[],
 *     byProject: { [projectId: string]: string[] },
 *   }
 *
 * Returned helpers are stable references (memoised on userInfo.permissions) so
 * they can be safely added to React hook dependency arrays without triggering
 * unnecessary re-renders.
 */
const usePermission = () => {
  const { userInfo } = useUser();
  const ctx = userInfo?.permissions || null;

  const can = useCallback(
    (permissionCode, projectId = null) => {
      if (!permissionCode) return false;
      if (!ctx) return false;
      if (ctx.isDeveloperAdmin) return true;
      const byProject = ctx.byProject || {};
      if (projectId === null || projectId === undefined) {
        return Object.values(byProject).some(
          (perms) => Array.isArray(perms) && perms.includes(permissionCode)
        );
      }
      const perms = byProject[String(projectId)] || [];
      return perms.includes(permissionCode);
    },
    [ctx]
  );

  const canAny = useCallback(
    (permissionCodes, projectId = null) => {
      if (!Array.isArray(permissionCodes) || permissionCodes.length === 0) return false;
      return permissionCodes.some((code) => can(code, projectId));
    },
    [can]
  );

  const isDeveloperAdmin = useCallback(() => Boolean(ctx?.isDeveloperAdmin), [ctx]);
  const persona = useCallback(() => ctx?.persona || null, [ctx]);
  const projectIds = useCallback(() => (Array.isArray(ctx?.projectIds) ? ctx.projectIds : []), [ctx]);

  const isPersona = useCallback(
    (...candidates) => {
      const p = ctx?.persona || null;
      return candidates.includes(p);
    },
    [ctx]
  );

  const isBvAssociate = useCallback(() => (ctx?.persona || null) === PERSONA.BV_ASSOCIATE, [ctx]);
  const isInteriorDesigner = useCallback(
    () => (ctx?.persona || null) === PERSONA.INTERIOR_DESIGNER,
    [ctx]
  );
  const isPropertyBuyer = useCallback(() => (ctx?.persona || null) === PERSONA.PROPERTY_BUYER, [ctx]);
  const isChannelPartner = useCallback(
    () => (ctx?.persona || null) === PERSONA.CHANNEL_PARTNER,
    [ctx]
  );

  return useMemo(
    () => ({
      can,
      canAny,
      isDeveloperAdmin,
      persona,
      projectIds,
      isPersona,
      isBvAssociate,
      isInteriorDesigner,
      isPropertyBuyer,
      isChannelPartner,
    }),
    [
      can,
      canAny,
      isDeveloperAdmin,
      persona,
      projectIds,
      isPersona,
      isBvAssociate,
      isInteriorDesigner,
      isPropertyBuyer,
      isChannelPartner,
    ]
  );
};

export default usePermission;
